import { useMemo } from "react";
import Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart } from "@highcharts/react";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { Row, Col } from "react-bootstrap";
import { useFetch } from "../../../hooks/api";
import moment from "moment";
import { formatPercent } from "../../../utils/formatNumber";

import { keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const CancelRateChart = ({ pricingPlan, marketPlace, start, end, group }) => {
  const { t } = useTranslation(["pricingv2"]);
  const filters = {
    start_date: moment(start).format("YYYY-MM-DD"),
    end_date: moment(end).format("YYYY-MM-DD"),
    group: group,
    pricing_plan_id: pricingPlan?.id,
  };
  const { data, isLoading } = useFetch(
    ["cancel_rate", marketPlace.marketPlace, start, end, group],
    `/pricing/cancelrate`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { cancelRateOptions, cancelRate } = useMemo(() => {
    if (!data) {
      return {};
    }

    const shipped = [];
    const cancelled = [];
    const pending = [];
    const sub_save = [];

    let i = 0;
    let minShipped = null;
    let minCancelled = null;
    let minPending = null;

    let numCancelledOrders = 0;
    let numOrders = 0;

    data.forEach((d) => {
      const dt = Date.UTC(parseInt(d.date.slice(0, 4)), parseInt(d.date.slice(5, 7)), parseInt(d.date.slice(8, 10)));

      if (i === 0) {
        minShipped = dt;
        minCancelled = dt;
        minPending = dt;
      }
      shipped.push([Date.parse(d.date), d.Shipped]);
      cancelled.push([Date.parse(d.date), d.Cancelled]);
      pending.push([Date.parse(d.date), d.Pending]);
      sub_save.push([Date.parse(d.date), d.sub_save]);

      numCancelledOrders += d.Cancelled;
      numOrders += d.Shipped + d.Cancelled + d.Pending;

      i++;
    });

    const groupModifier = 7;

    let cancelRate = 0;
    if (numOrders > 0) {
      cancelRate = numCancelledOrders / numOrders;
    }

    return {
      cancelRate: cancelRate.toFixed(2),
      cancelRateOptions: {
        chart: {
          type: "column",
          style: {
            fontSize: "14px",
          },
        },
        title: {
          text: null,
        },
        xAxis: {
          type: "datetime",
          dateTimeLabelFormats: {
            day: "%e. %b",
          },
          title: {
            text: t(($) => $.pricingv2["Date"]),
          },
        },
        yAxis: [
          {
            min: 0,
            title: {
              text: t(($) => $.pricingv2["Number of Orders"]),
            },
            stackLabels: {
              enabled: true,
              style: {
                fontWeight: "bold",
                color: Highcharts.defaultOptions.title.style && Highcharts.defaultOptions.title.style.color,
              },
            },
          },
          {
            min: 0,
            opposite: true,
            title: {
              text: t(($) => $.pricingv2["Price"]),
            },
            stackLabels: {
              enabled: true,
              style: {
                fontWeight: "bold",
                color: Highcharts.defaultOptions.title.style && Highcharts.defaultOptions.title.style.color,
              },
            },
          },
        ],
        tooltip: {
          headerFormat: "<b>{point.x:%e. %b}</b><br/>",
          pointFormat: `{series.name}: {point.y}<br/>${t(($) => $.pricingv2["Total"])}: {point.stackTotal}`,
        },
        plotOptions: {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
            },
          },
        },
        series: [
          {
            name: t(($) => $.pricingv2["Shipped Orders"]),
            color: trellisPalette[2],
            data: shipped,
            pointStart: minShipped,
            pointInterval: 24 * 3600 * 1000 * groupModifier, // one day
          },
          {
            name: t(($) => $.pricingv2["Cancelled Orders"]),
            color: trellisPalette[13],
            data: cancelled,
            pointStart: minCancelled,
            pointInterval: 24 * 3600 * 1000 * groupModifier, // one day
          },
          {
            name: t(($) => $.pricingv2["Pending Orders"]),
            color: trellisPalette[14],
            data: pending,
            pointStart: minPending,
            pointInterval: 24 * 3600 * 1000 * groupModifier, // one day
          },
          {
            name: t(($) => $.pricingv2["Subscribe - Save Orders"]),
            type: "spline",
            color: trellisPalette[0],
            yAxis: 1,
            data: sub_save,
            pointStart: minPending,
            pointInterval: 24 * 3600 * 1000 * groupModifier, // one day
          },
        ],
      },
    };
  }, [data, t]);

  return (
    <Row>
      <Col>
        {!isLoading && (
          <>
            <Row>
              <Col className={"mt-3 mx-3"}>
                <h5>
                  {t(($) => $.pricingv2["Cancel Rate: {pct}"], {
                    pct: formatPercent(cancelRate, { decimalPlaces: 0 }),
                  })}
                </h5>
              </Col>
            </Row>

            <Chart options={cancelRateOptions} />
          </>
        )}
      </Col>
    </Row>
  );
};

export default CancelRateChart;
